import React, { useCallback, useEffect, useState } from 'react';
import { PageContainer, PageHeader } from '@/components/layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertCircle, AlertTriangle, RefreshCw, Send } from 'lucide-react';
import { adminMemberBillingStatusApi, WorkspaceMemberBillingStatusItem, PlanCode } from '@/api/planApi';
import { LoadingBlock } from '@/components/ui/spinner';
import { parseServerTime } from '@/lib/serverTime';

// ── 상수 ─────────────────────────────────────────────────────
// 개인 워크스페이스 재조정 버그(2026-09-12)로 결제요청/자동취소가 조용히 누락됐던
// 사고 재발 방지용 - 유료 플랜 워크스페이스를 회원 단위로 상시 모니터링한다.

const PLAN_NAMES: Record<PlanCode, string> = {
  FREE: '무료',
  STARTER: '스타트',
  PROFESSIONAL: '프로',
  ENTERPRISE: '엔터프라이즈',
};

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: '정상',
  PAST_DUE: '미납(유예중)',
  INACTIVE: '비활성',
};

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-700 border-green-200',
  PAST_DUE: 'bg-red-100 text-red-700 border-red-200',
  INACTIVE: 'bg-gray-100 text-gray-500 border-gray-200',
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  NONE: '기록 없음',
  PENDING: '입금대기',
  CONFIRMED: '입금완료',
  CANCELLED: '취소',
};

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  NONE: 'bg-red-100 text-red-700 border-red-200',
  PENDING: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  CONFIRMED: 'bg-green-100 text-green-700 border-green-200',
  CANCELLED: 'bg-gray-100 text-gray-500 border-gray-200',
};

type FilterTab = 'ALL' | 'GAP' | 'PAST_DUE';

const FILTER_TABS: { key: FilterTab; label: string }[] = [
  { key: 'GAP', label: '이상 상태만' },
  { key: 'PAST_DUE', label: '미납(유예중)만' },
  { key: 'ALL', label: '전체' },
];

// ── 컴포넌트 ─────────────────────────────────────────────────

export default function AdminMemberBillingStatus() {
  const [items, setItems] = useState<WorkspaceMemberBillingStatusItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<FilterTab>('GAP');
  const [reissuingId, setReissuingId] = useState<number | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminMemberBillingStatusApi.getList();
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError('유료회원 결제 현황을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleReissue = async (item: WorkspaceMemberBillingStatusItem) => {
    if (!window.confirm(`${item.ownerName ?? item.ownerUserId}님에게 계좌이체 입금요청(${item.price.toLocaleString()}원)을 재발송하시겠습니까?`)) {
      return;
    }
    setReissuingId(item.workspaceId);
    setError(null);
    setActionMessage(null);
    try {
      await adminMemberBillingStatusApi.reissuePaymentRequest(item.workspaceId);
      setActionMessage(`${item.ownerName ?? item.ownerUserId}님에게 입금요청을 재발송했습니다.`);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? '입금요청 재발송에 실패했습니다.');
    } finally {
      setReissuingId(null);
    }
  };

  const filteredItems = items.filter((item) => {
    if (activeTab === 'GAP') return item.billingGap;
    if (activeTab === 'PAST_DUE') return item.status === 'PAST_DUE';
    return true;
  });

  const gapCount = items.filter((item) => item.billingGap).length;

  const formatDateTime = (iso: string | null) => {
    const date = parseServerTime(iso);
    return date
      ? date.toLocaleString('ko-KR', {
          year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
        })
      : '-';
  };

  return (
    <PageContainer maxWidth="full">
      <PageHeader
        title="유료회원 결제 현황"
        description="유료 플랜 워크스페이스별 이번 달 결제기록/상태를 회원 단위로 모니터링합니다. 이번 달 결제기록이 전혀 없는데 상태가 정상(ACTIVE)이면 결제요청 자체가 누락된 이상 상태입니다."
        action={
          <div className="flex items-center gap-2">
            {gapCount > 0 && (
              <Badge className="bg-red-100 text-red-700 border border-red-300 text-sm px-3 py-1">
                이상 상태 {gapCount}건
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              새로고침
            </Button>
          </div>
        }
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {actionMessage && (
        <Alert>
          <AlertDescription>{actionMessage}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-4">
          <div className="flex gap-1">
            {FILTER_TABS.map((tab) => (
              <Button
                key={tab.key}
                variant={activeTab === tab.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab(tab.key)}
                className="text-xs"
              >
                {tab.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <LoadingBlock size="lg" py={16} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>회원</TableHead>
                    <TableHead>구분</TableHead>
                    <TableHead>요금제</TableHead>
                    <TableHead className="text-center">상태</TableHead>
                    <TableHead>결제수단</TableHead>
                    <TableHead>결제일</TableHead>
                    <TableHead className="text-center">이번 달 결제기록</TableHead>
                    <TableHead>이번 달 결제 생성 시각</TableHead>
                    <TableHead>미납 시작 시각</TableHead>
                    <TableHead className="text-center">관리</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-gray-400 py-16 text-sm">
                        해당하는 회원이 없습니다.
                      </TableCell>
                    </TableRow>
                  ) : filteredItems.map((item) => (
                    <TableRow
                      key={item.workspaceId}
                      className={item.billingGap ? 'bg-red-50/60' : undefined}
                    >
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {item.billingGap && <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />}
                          <div>
                            <p className="text-sm font-medium">{item.ownerName ?? item.ownerUserId}</p>
                            <p className="text-xs text-gray-500">{item.ownerEmail ?? item.ownerUserId}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">
                        {item.personal ? '개인' : (item.teamName ?? '팀')}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{PLAN_NAMES[item.plan] ?? item.plan}</span>
                        <span className="text-xs text-gray-500 ml-1">{item.price.toLocaleString()}원</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={`text-xs border ${STATUS_COLORS[item.status] ?? 'bg-gray-100 text-gray-600'}`}>
                          {STATUS_LABELS[item.status] ?? item.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">
                        {item.paymentMethod === 'CARD' ? '카드' : item.paymentMethod === 'BANK_TRANSFER' ? '계좌이체' : '-'}
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">
                        {item.billingCycleDay ? `매월 ${item.billingCycleDay}일` : '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={`text-xs border ${PAYMENT_STATUS_COLORS[item.currentMonthPaymentStatus]}`}>
                          {PAYMENT_STATUS_LABELS[item.currentMonthPaymentStatus]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">
                        {formatDateTime(item.currentMonthPaymentCreatedAt)}
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">
                        {formatDateTime(item.pastDueSince)}
                      </TableCell>
                      <TableCell className="text-center">
                        {(item.currentMonthPaymentStatus === 'NONE' || item.currentMonthPaymentStatus === 'CANCELLED') && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs h-7"
                            disabled={reissuingId === item.workspaceId}
                            onClick={() => handleReissue(item)}
                          >
                            <Send className="w-3 h-3 mr-1" />
                            {reissuingId === item.workspaceId ? '발송 중...' : '입금요청 재발송'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}
